import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  CalendarDays, Clock, Users, Loader2, CheckCircle2, Hourglass, ChevronLeft, Utensils, Scissors, AlertCircle,
} from 'lucide-react';

const todayStr = () => new Date().toISOString().slice(0, 10);
const fmtDateLong = (s, locale) => {
  try { return new Date(s + 'T00:00:00').toLocaleDateString(locale || 'it-IT', { weekday: 'long', day: 'numeric', month: 'long' }); }
  catch { return s; }
};

export default function PublicBooking() {
  const { t, i18n } = useTranslation();
  const { rid } = useParams();
  const [config, setConfig] = useState(null);
  const [loadingCfg, setLoadingCfg] = useState(true);
  const [cfgError, setCfgError] = useState(null);

  const [date, setDate] = useState(todayStr());
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [closed, setClosed] = useState(false);
  const [slot, setSlot] = useState(null);

  const [step, setStep] = useState(1); // 1 data+slot, 2 form, 3 esito
  const [people, setPeople] = useState(2);
  const [serviceId, setServiceId] = useState('');
  const [form, setForm] = useState({ name: '', surname: '', phone: '', email: '', preferences: '', allergens: '' });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [formError, setFormError] = useState('');

  const isServices = config?.model === 'servizi';
  const maxDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() + (config?.advance_days || 30));
    return d.toISOString().slice(0, 10);
  })();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/reservations/config/${rid}`);
        setConfig(data);
        if (data.services && data.services.length) setServiceId(data.services[0].id);
      } catch (e) {
        setCfgError(t('booking.unavailable'));
      } finally { setLoadingCfg(false); }
    })();
  }, [rid, t]);

  const loadSlots = useCallback(async (d) => {
    setLoadingSlots(true); setSlot(null);
    try {
      const { data } = await api.get(`/public/reservations/availability/${rid}`, { params: { date: d } });
      setSlots(data.slots || []);
      setClosed(!!data.closed);
    } catch (e) { setSlots([]); }
    finally { setLoadingSlots(false); }
  }, [rid]);

  useEffect(() => { if (config && config.enabled) loadSlots(date); }, [date, config, loadSlots]);

  const submit = async () => {
    setFormError('');
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim()) {
      setFormError(t('booking.errRequired')); return;
    }
    if (isServices && !serviceId) { setFormError(t('booking.errService')); return; }
    setSubmitting(true);
    try {
      const payload = {
        date, slot_start: slot,
        people: isServices ? 1 : Number(people),
        service_id: isServices ? serviceId : null,
        customer: { name: form.name.trim(), surname: form.surname.trim(), phone: form.phone.trim(), email: form.email.trim() },
        preferences: form.preferences.trim(),
        allergens: isServices ? '' : form.allergens.trim(),
      };
      const { data } = await api.post(`/public/reservations/${rid}`, payload);
      setResult(data);
      setStep(3);
    } catch (e) {
      setFormError(e?.response?.data?.detail || t('booking.errSubmit'));
    } finally { setSubmitting(false); }
  };

  const reset = () => {
    setStep(1); setResult(null); setSlot(null);
    setForm({ name: '', surname: '', phone: '', email: '', preferences: '', allergens: '' });
    setPeople(2); loadSlots(date);
  };

  // ---------- stati globali ----------
  if (loadingCfg) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-7 w-7 animate-spin text-slate-400" /></div>;
  }
  if (cfgError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-6 text-center">
        <AlertCircle className="h-10 w-10 text-slate-300 mb-2" />
        <div className="text-slate-700 font-medium">{cfgError}</div>
      </div>
    );
  }
  const biz = config.business || {};

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4 flex justify-center" data-testid="public-booking">
      <div className="w-full max-w-xl">
        {/* Header attivita */}
        <div className="text-center mb-5">
          {biz.logo ? (
            <img src={biz.logo} alt={biz.name} className="mx-auto max-h-16 object-contain mb-2" />
          ) : null}
          <h1 className="text-2xl font-bold text-slate-800" data-testid="booking-business-name">{biz.name || t('booking.title')}</h1>
          <p className="text-sm text-slate-500 flex items-center justify-center gap-1.5 mt-1">
            {isServices ? <Scissors className="h-4 w-4" /> : <Utensils className="h-4 w-4" />}
            {isServices ? t('booking.bookAppointment') : t('booking.bookTable')}
          </p>
        </div>

        {!config.enabled ? (
          <div className="rounded-2xl bg-white border border-slate-200 p-8 text-center text-slate-500">
            {t('booking.notActive')}
          </div>
        ) : (
          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-5">
            {/* STEP 1 */}
            {step === 1 && (
              <div className="space-y-5">
                <div>
                  <Label className="flex items-center gap-1.5 mb-1.5"><CalendarDays className="h-4 w-4 text-slate-500" /> {t('booking.date')}</Label>
                  <Input type="date" value={date} min={todayStr()} max={maxDate} onChange={(e) => setDate(e.target.value)} data-testid="booking-date-input" />
                  <p className="text-sm text-slate-500 mt-1 capitalize">{fmtDateLong(date, i18n.language)}</p>
                </div>

                <div>
                  <Label className="flex items-center gap-1.5 mb-2"><Clock className="h-4 w-4 text-slate-500" /> {t('booking.time')}</Label>
                  {loadingSlots ? (
                    <div className="py-6 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
                  ) : closed ? (
                    <div className="py-4 text-center text-slate-500 text-sm">{t('booking.closedDate')}</div>
                  ) : slots.length === 0 ? (
                    <div className="py-4 text-center text-slate-500 text-sm">{t('booking.noSlots')}</div>
                  ) : (
                    <div className="grid grid-cols-4 gap-2" data-testid="booking-slots">
                      {slots.map((s) => (
                        <button
                          key={s.time}
                          disabled={!s.available}
                          onClick={() => setSlot(s.time)}
                          data-testid={`slot-${s.time}`}
                          className={[
                            'h-11 rounded-xl text-sm font-medium border transition-colors',
                            !s.available ? 'opacity-40 cursor-not-allowed border-slate-200 text-slate-400 line-through' :
                              slot === s.time ? 'bg-slate-800 text-white border-slate-800' : 'border-slate-200 text-slate-700 hover:border-slate-800',
                          ].join(' ')}
                        >{s.time}</button>
                      ))}
                    </div>
                  )}
                </div>

                <Button className="w-full h-12 rounded-xl" disabled={!slot} onClick={() => setStep(2)} data-testid="booking-next-button">
                  {t('booking.continue')}
                </Button>
              </div>
            )}

            {/* STEP 2 */}
            {step === 2 && (
              <div className="space-y-4">
                <button onClick={() => setStep(1)} className="text-sm text-slate-500 flex items-center gap-1 hover:text-slate-800"><ChevronLeft className="h-4 w-4" /> {t('booking.back')}</button>
                <div className="rounded-xl bg-slate-50 px-4 py-2.5 text-sm text-slate-600 flex items-center gap-2">
                  <CalendarDays className="h-4 w-4" /> <span className="capitalize">{fmtDateLong(date, i18n.language)}</span> · <Clock className="h-4 w-4" /> {slot}
                </div>

                {isServices ? (
                  <div>
                    <Label>{t('booking.service')}</Label>
                    <Select value={serviceId} onValueChange={setServiceId}>
                      <SelectTrigger className="mt-1" data-testid="booking-service-select"><SelectValue placeholder={t('booking.chooseService')} /></SelectTrigger>
                      <SelectContent>
                        {(config.services || []).map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.name}{s.duration_minutes ? ` (${s.duration_minutes} min)` : ''}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div>
                    <Label className="flex items-center gap-1.5"><Users className="h-4 w-4 text-slate-500" /> {t('booking.people')}</Label>
                    <div className="flex items-center gap-3 mt-1.5">
                      <Button type="button" variant="outline" className="h-11 w-11 rounded-xl text-lg" onClick={() => setPeople((p) => Math.max(1, p - 1))} data-testid="people-minus">−</Button>
                      <span className="text-xl font-semibold w-10 text-center" data-testid="people-count">{people}</span>
                      <Button type="button" variant="outline" className="h-11 w-11 rounded-xl text-lg" onClick={() => setPeople((p) => p + 1)} data-testid="people-plus">+</Button>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{t('booking.name')}</Label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="booking-name" />
                  </div>
                  <div>
                    <Label>{t('booking.surname')}</Label>
                    <Input value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })} data-testid="booking-surname" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{t('booking.phone')}</Label>
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="booking-phone" />
                  </div>
                  <div>
                    <Label>{t('booking.email')}</Label>
                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="booking-email" />
                  </div>
                </div>

                <div>
                  <Label>{t('booking.preferences')}</Label>
                  <Textarea rows={2} value={form.preferences} onChange={(e) => setForm({ ...form, preferences: e.target.value })} placeholder={isServices ? t('booking.prefPlaceholderServices') : t('booking.prefPlaceholderTable')} data-testid="booking-preferences" />
                </div>
                {!isServices && (
                  <div>
                    <Label>{t('booking.allergens')}</Label>
                    <Textarea rows={2} value={form.allergens} onChange={(e) => setForm({ ...form, allergens: e.target.value })} data-testid="booking-allergens" />
                  </div>
                )}

                {formError ? <div className="text-sm text-rose-600 flex items-center gap-1.5"><AlertCircle className="h-4 w-4" /> {formError}</div> : null}

                <Button className="w-full h-12 rounded-xl" onClick={submit} disabled={submitting} data-testid="booking-submit-button">
                  {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : t('booking.confirm')}
                </Button>
              </div>
            )}

            {/* STEP 3 - esito */}
            {step === 3 && result && (
              <div className="text-center py-4" data-testid="booking-result">
                {result.status === 'confirmed' ? (
                  <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-3" />
                ) : (
                  <Hourglass className="h-14 w-14 text-amber-500 mx-auto mb-3" />
                )}
                <h2 className="text-xl font-bold text-slate-800">
                  {result.status === 'confirmed' ? t('booking.confirmedTitle') : t('booking.receivedTitle')}
                </h2>
                <p className="text-slate-500 text-sm mt-1">
                  {result.status === 'confirmed'
                    ? t('booking.confirmedDesc')
                    : t('booking.receivedDesc')}
                </p>
                <div className="mt-4 rounded-xl bg-slate-50 p-4 text-sm text-slate-700 inline-block text-left">
                  <div className="capitalize">{fmtDateLong(result.date, i18n.language)} · {result.slot_start}</div>
                  {isServices ? <div>{t('booking.service')}: {result.service_name}</div> : <div>{t('booking.peopleLabel')}: {result.people}</div>}
                </div>
                <div className="mt-5">
                  <Button variant="outline" className="rounded-xl" onClick={reset} data-testid="booking-new-button">{t('booking.newBooking')}</Button>
                </div>
              </div>
            )}
          </div>
        )}

        <p className="text-center text-xs text-slate-400 mt-4">{t('booking.footer')}</p>
      </div>
    </div>
  );
}
